import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { SparklesIcon, ZapIcon, ShieldIcon, LayersIcon, BoxIcon } from 'lucide-react';
type Service = {
  icon: BoxIcon;
  title: string;
  description: string;
  gradient: string;
};
const services: Service[] = [{
  icon: SparklesIcon,
  title: 'Iridescent Effects',
  description: 'Dynamic color-shifting gradients that respond to user interaction and create depth.',
  gradient: 'from-blue-500/20 to-cyan-500/20'
}, {
  icon: ZapIcon,
  title: 'Lightning Fast',
  description: 'Optimized animations running at 60fps with hardware acceleration for smooth experiences.',
  gradient: 'from-cyan-500/20 to-purple-500/20'
}, {
  icon: ShieldIcon,
  title: 'Accessible Design',
  description: 'Built with accessibility in mind, supporting reduced motion and screen readers.',
  gradient: 'from-purple-500/20 to-pink-500/20'
}, {
  icon: LayersIcon,
  title: 'Layered Depth',
  description: 'Multi-dimensional glass layers create realistic depth and spatial hierarchy.',
  gradient: 'from-pink-500/20 to-blue-500/20'
}];
type ServiceCardProps = {
  service: Service;
  index: number;
};
function ServiceCard({
  service,
  index
}: ServiceCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [rotateX, setRotateX] = useState(0);
  const [rotateY, setRotateY] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const mouseX = e.clientX - centerX;
    const mouseY = e.clientY - centerY;
    // Calculate rotation (max 10 degrees)
    const rotateXValue = mouseY / (rect.height / 2) * -8;
    const rotateYValue = mouseX / (rect.width / 2) * 8;
    setRotateX(rotateXValue);
    setRotateY(rotateYValue);
  };
  const handleMouseLeave = () => {
    setRotateX(0);
    setRotateY(0);
    setIsHovered(false);
  };
  const Icon = service.icon;
  return <motion.article ref={cardRef} initial={{
    opacity: 0,
    y: 30,
    filter: 'blur(10px)'
  }} whileInView={{
    opacity: 1,
    y: 0,
    filter: 'blur(0px)'
  }} viewport={{
    once: true,
    margin: '-50px'
  }} transition={{
    duration: 0.6,
    delay: index * 0.1,
    ease: [0.16, 1, 0.3, 1]
  }} onMouseMove={handleMouseMove} onMouseEnter={() => setIsHovered(true)} onMouseLeave={handleMouseLeave} style={{
    transform: `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
    transformStyle: 'preserve-3d'
  }} className="card-3d relative group cursor-default" data-id="element-109">
      <div className="gradient-border rounded-2xl h-full" data-id="element-110">
        <div className="glass rounded-2xl p-8 h-full relative overflow-hidden refraction" data-id="element-111">
          {/* Gradient Background */}
          <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} aria-hidden="true" data-id="element-112" />

          {/* Highlight on hover */}
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" style={{
          background: isHovered ? `radial-gradient(circle at ${50 + rotateY * 3}% ${50 - rotateX * 3}%, rgba(255,255,255,0.1) 0%, transparent 50%)` : 'none'
        }} aria-hidden="true" data-id="element-113" />

          {/* Content */}
          <div className="relative z-10" style={{
          transform: 'translateZ(20px)'
        }} data-id="element-114">
            {/* Icon */}
            <div className="w-14 h-14 rounded-xl glass flex items-center justify-center mb-6 group-hover:shadow-lg group-hover:shadow-blue-500/20 transition-shadow duration-300" data-id="element-115">
              <Icon className="w-7 h-7 text-cyan-400" data-id="element-116" />
            </div>

            {/* Title */}
            <h3 className="text-xl font-semibold text-white mb-3" data-id="element-117">
              {service.title}
            </h3>

            {/* Description */}
            <p className="text-gray-400 leading-relaxed" data-id="element-118">
              {service.description}
            </p>
          </div>
        </div>
      </div>
    </motion.article>;
}
export function ServicesSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, {
    once: true,
    margin: '-100px'
  });
  return <section ref={sectionRef} className="relative py-32 px-4" aria-labelledby="services-heading" data-id="element-119">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-500/[0.02] to-transparent" aria-hidden="true" data-id="element-120" />

      <div className="max-w-6xl mx-auto" data-id="element-121">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-122">
          <motion.span initial={{
          opacity: 0,
          scale: 0.9
        }} animate={isInView ? {
          opacity: 1,
          scale: 1
        } : {}} transition={{
          duration: 0.5
        }} className="inline-block px-4 py-1.5 rounded-full glass text-sm text-cyan-400 mb-4" data-id="element-123">
            Features
          </motion.span>
          <h2 id="services-heading" className="text-3xl md:text-5xl font-bold mb-4" data-id="element-124">
            <span className="text-white" data-id="element-125">Built for the </span>
            <span className="text-holo" data-id="element-126">Future</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto" data-id="element-127">
            Every component is crafted with precision, combining cutting-edge
            visual effects with performance and accessibility.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-128">
          {services.map((service, index) => <ServiceCard key={service.title} service={service} index={index} data-id="element-129" />)}
        </div>
      </div>
    </section>;
}